import React, { useState } from 'react';
import api from '../lib/api';
import { X, Check, Loader2, CalendarDays } from 'lucide-react';

const SIZES = ['1-50', '51-200', '201-1,000', '1,001-5,000', '5,001+'];

export const BookDemoModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', company: '', company_size: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/api/demo-requests', { ...form, source: 'landing_page' });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => { setSent(false); setForm({ name: '', email: '', company: '', company_size: '', phone: '', message: '' }); }, 300);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onMouseDown={close} data-testid="book-demo-backdrop" />
      <div className="relative w-full max-w-lg bg-[#0B0F1A] border border-slate-800 rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.6)] max-h-[92vh] overflow-y-auto" data-testid="book-demo-modal">
        <button onClick={close} className="absolute top-4 right-4 p-2 text-slate-500 hover:text-slate-200 transition-colors" data-testid="book-demo-close-btn">
          <X className="w-5 h-5" />
        </button>

        {sent ? (
          <div className="p-10 text-center" data-testid="book-demo-success">
            <div className="w-14 h-14 rounded-2xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center mx-auto mb-5">
              <Check className="w-7 h-7 text-blue-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-50">Request received</h3>
            <p className="text-sm text-slate-400 mt-3 leading-relaxed">
              Thanks {form.name.split(' ')[0] || 'there'} — our team will reach out within one business day to schedule your walkthrough of Altrunic.
            </p>
            <button onClick={close} className="mt-7 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-3 rounded-xl text-sm transition-colors" data-testid="book-demo-done-btn">
              Back to site
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8" data-testid="book-demo-form">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 border border-slate-700/60 mb-5">
              <CalendarDays className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-[11px] font-semibold text-blue-400 tracking-[0.15em] uppercase font-mono">Enterprise demo</span>
            </div>
            <h3 className="text-2xl font-bold text-slate-50 tracking-tight">Book a demo</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              A 30-minute walkthrough of campaigns, employer matching and impact reporting — tailored to your company size.
            </p>

            <div className="mt-6 space-y-3.5">
              <div className="grid sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Full name *</label>
                  <input required value={form.name} onChange={update('name')} placeholder="Jane Whitfield"
                    className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-blue-500 transition-colors"
                    data-testid="demo-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Work email *</label>
                  <input required type="email" value={form.email} onChange={update('email')} placeholder="jane@company.com"
                    className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-blue-500 transition-colors"
                    data-testid="demo-email-input" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Company *</label>
                  <input required value={form.company} onChange={update('company')} placeholder="Acme Corp"
                    className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-blue-500 transition-colors"
                    data-testid="demo-company-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Company size</label>
                  <select value={form.company_size} onChange={update('company_size')}
                    className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-blue-500 transition-colors"
                    data-testid="demo-size-select">
                    <option value="">Select…</option>
                    {SIZES.map(s => <option key={s} value={s}>{s} employees</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1.5">Phone <span className="text-slate-600 font-normal">(optional)</span></label>
                <input value={form.phone} onChange={update('phone')} placeholder="+1 555 000 1234"
                  className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-blue-500 transition-colors"
                  data-testid="demo-phone-input" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1.5">What would you like to cover?</label>
                <textarea rows={3} value={form.message} onChange={update('message')} placeholder="We're planning a matching program for 800 employees…"
                  className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  data-testid="demo-message-input" />
              </div>
            </div>

            {error && (
              <p className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3" data-testid="demo-form-error">{error}</p>
            )}

            <button type="submit" disabled={loading}
              className="mt-6 w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-xl text-sm transition-all duration-200 hover:shadow-xl hover:shadow-blue-600/20"
              data-testid="demo-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : 'Request my demo'}
            </button>
            <p className="mt-3 text-xs text-slate-600 text-center">No sales pressure. We'll reply within one business day.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookDemoModal;
